import {
  ReviewImportCard,
  ReviewSettingsCard,
  ReviewStorefrontCard,
  ReviewTable,
} from "@/components/dashboard/integrations/ebay/ebay-reviews";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { buttonVariants } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import GatedOverlay from "@/components/ui/gated-overlay";
import { accountQueryOpts } from "@/hooks/ebay";
import { reviewConfigQueryOpts } from "@/hooks/ebay-reviews";
import { isGated } from "@/hooks/gated";
import { billingQueryOpts, useBilling } from "@/hooks/use-billing";
import { cn, getOrgIdWithFallback } from "@/lib/utils";
import { useAuth } from "@/providers/auth";
import { useOrg } from "@/providers/organization";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/$org/integrations/ebay/$alias/reviews")({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const { getToken, setToken, handleTokenInvalid } = context.auth;
    const orgId = getOrgIdWithFallback(context.org.orgId);
    const authParams = { setToken, token: getToken() as string, orgId };

    const gated = await isGated(authParams, handleTokenInvalid);
    // the page is greyed out without the plan, so it's known before it shows
    await context.queryClient.prefetchQuery(billingQueryOpts(authParams));

    const account = await context.queryClient.ensureQueryData(
      accountQueryOpts(params.alias, authParams),
    );

    await context.queryClient.ensureQueryData(
      reviewConfigQueryOpts(account.platform_connection_id, authParams),
    );

    return { gated: gated?.is_gated };
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const { gated } = Route.useLoaderData();
  const { org, alias } = Route.useParams();
  const { orgId } = useOrg();
  const { getToken, setToken } = useAuth();
  const queryClient = useQueryClient();
  const authParams = {
    setToken,
    token: getToken() as string,
    orgId: getOrgIdWithFallback(orgId),
  };

  const { data: account } = useQuery(accountQueryOpts(alias, authParams));
  const pcId = account?.platform_connection_id ?? "";
  const { data: config, isError } = useQuery({
    ...reviewConfigQueryOpts(pcId, authParams),
    enabled: !!pcId,
  });
  // reviews aren't part of the free plan
  const plan = useBilling().data?.plan_name;
  const reviewsAllowed = !!plan && plan !== "Free";

  // the reviews are listed again once an import finishes
  const status = config?.import_status;
  const previousStatus = useRef(status);
  useEffect(() => {
    if (previousStatus.current === "RUNNING" && status !== "RUNNING") {
      void queryClient.invalidateQueries({
        queryKey: ["ebayReviews", pcId, "list"],
      });
    }
    previousStatus.current = status;
  }, [status, pcId, queryClient]);

  if (gated) {
    return <GatedOverlay org={org} />;
  }

  return (
    <div className="mx-auto max-w-6xl">
      {isError && !config && (
        <p className="text-destructive text-sm">{t("reviews.loadFailed")}</p>
      )}
      {account && config && (
        <div className="space-y-8">
          {!reviewsAllowed && (
            <Alert>
              <Lock />
              <AlertTitle>{t("reviews.plan.title")}</AlertTitle>
              <AlertDescription>
                <p>{t("reviews.plan.description")}</p>
                <Link
                  to="/$org/settings/billing"
                  params={{ org }}
                  className={cn(
                    buttonVariants({ variant: "link" }),
                    "h-auto p-0",
                  )}
                >
                  {t("reviews.plan.viewPlans")}
                </Link>
              </AlertDescription>
            </Alert>
          )}
          <div
            className={cn(
              "space-y-8",
              !reviewsAllowed && "pointer-events-none opacity-50 select-none",
            )}
            inert={!reviewsAllowed}
          >
            <ReviewImportCard config={config} />
            <ReviewSettingsCard config={config} />
            <ReviewStorefrontCard config={config} />
            <Card>
              <CardContent>
                <ReviewTable pcId={pcId} />
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
